import React, { useEffect, useMemo, useState } from "react";
import { Loader2, FolderPlus, Trash2, Pencil, ArrowRightLeft, History, Sprout, X, Check, Printer } from "lucide-react";
import { toast } from "sonner";
import api from "../../console/api";

/* Final stock allocation , every audited tuber gets a destination.
   Available to Sell is never allocated into: it's the live remainder. */

const EDGE = "#E8DFCB";
const AUB = "#3E1F46";

export const useAllocation = () => {
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    try {
      const r = await api.get("/admin/allocation");
      setBoard(r.data);
    } catch { toast.error("Couldn't load the allocation board."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  return { board, loading, reload: load };
};

const Stat = ({ label, value, tone = AUB, testid }) => (
  <div className="bg-white border rounded p-3" style={{ borderColor: EDGE }}>
    <div className="text-[9px] tracking-[0.22em] uppercase text-[#9C8B73]">{label}</div>
    <div className="font-serif text-2xl mt-0.5" style={{ color: tone }} data-testid={testid}>{value}</div>
  </div>
);

export const AllocationSummary = ({ summary }) => {
  if (!summary) return null;
  return (
    <section className="space-y-2" data-testid="allocation-summary">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        <Stat label="Total audited" value={summary.total_audited} testid="alloc-total-audited" />
        <Stat label="To orders" value={summary.total_orders} tone="#6E1E3A" testid="alloc-total-orders" />
        <Stat label="To growing" value={summary.total_growing} tone="#2F6B4F" testid="alloc-total-growing" />
        <Stat label="Reserved" value={summary.total_reserved} tone="#8A5D12" testid="alloc-total-reserved" />
        <Stat label="Available to sell" value={summary.total_available} tone="#1F5B3F" testid="alloc-total-available" />
      </div>
      {summary.over_allocated_varieties > 0 ? (
        <p className="text-xs text-[#A33030]" data-testid="allocation-reconcile-warning">
          {summary.over_allocated_varieties} variet{summary.over_allocated_varieties === 1 ? "y is" : "ies are"} over-allocated , move stock back before you count on these numbers.
        </p>
      ) : (
        <p className="text-[11px] italic text-[#7A6A55]" data-testid="allocation-reconcile-ok">
          Every audited tuber has a destination , orders + growing + reserved + available reconciles back to {summary.total_audited}.
        </p>
      )}
      {summary.orders_pending > 0 && (
        <p className="text-xs text-[#8A5D12]" data-testid="allocation-orders-pending">
          {summary.orders_pending} tuber{summary.orders_pending === 1 ? "" : "s"} still needed by Pull List orders , allocate them on the Pull List.
        </p>
      )}
    </section>
  );
};

const btn = "inline-flex items-center gap-1.5 px-3 h-9 text-[10px] font-bold uppercase tracking-[0.12em]";

export const AllocationRows = ({ board, reload }) => {
  const [filter, setFilter] = useState("");
  const [allocating, setAllocating] = useState(null);
  const [history, setHistory] = useState(null);

  const rows = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    const list = board?.rows || [];
    return needle ? list.filter((r) => r.name.toLowerCase().includes(needle)) : list;
  }, [board, filter]);

  const openHistory = async (row) => {
    setHistory({ name: row.name, movements: null });
    try {
      const r = await api.get("/admin/allocation/history", { params: { name: row.name } });
      setHistory({ name: row.name, movements: r.data.movements || [] });
    } catch { setHistory({ name: row.name, movements: [] }); }
  };

  return (
    <section className="bg-white border rounded" style={{ borderColor: EDGE }} data-testid="allocation-rows">
      <div className="px-3 py-2 border-b flex items-center gap-2" style={{ borderColor: EDGE }}>
        <div className="text-[10px] tracking-[0.3em] uppercase font-semibold text-[#6E1E3A]">Where every tuber sits</div>
        <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter varieties…"
          data-testid="allocation-filter" className="ml-auto h-8 px-2 border rounded text-sm outline-none" style={{ borderColor: EDGE }} />
      </div>
      {rows.length === 0 ? (
        <div className="p-8 text-center text-sm text-[#7A6A55]" data-testid="allocation-empty">
          Nothing on the audit yet , count your varieties above first.
        </div>
      ) : (
        <div className="divide-y" style={{ borderColor: EDGE }}>
          {rows.map((r) => (
            <div key={r.variety_key} className="p-3" data-testid={`allocation-row-${r.variety_key}`}>
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <strong className="font-serif text-[17px] text-[#3C2A1E]">{r.name}</strong>
                <span className="text-[11px] uppercase tracking-[0.12em] text-[#9C8B73]">Final audit</span>
                <span className="font-serif text-xl" style={{ color: AUB }} data-testid={`alloc-final-${r.variety_key}`}>{r.final}</span>
                <span className="ml-auto flex gap-1.5">
                  <button type="button" onClick={() => setAllocating(r)} data-testid={`alloc-allocate-${r.variety_key}`}
                    className={btn + " text-white"} style={{ backgroundColor: "#6E1E3A" }}>
                    <Sprout className="w-3.5 h-3.5" /> Allocate
                  </button>
                  <button type="button" onClick={() => openHistory(r)} data-testid={`alloc-history-${r.variety_key}`}
                    className={btn + " border text-[#3C2A1E]"} style={{ borderColor: EDGE }}>
                    <History className="w-3.5 h-3.5" /> History
                  </button>
                </span>
              </div>
              <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-sm text-[#3C2A1E]">
                <span data-testid={`alloc-orders-${r.variety_key}`}>Customer orders <strong>{r.orders}</strong>
                  {r.orders_pending > 0 && <em className="text-[#8A5D12] text-xs"> · {r.orders_pending} still needed</em>}
                </span>
                {r.growing_lines.map((g) => (
                  <span key={g.folder_id + g.folder_name}>{g.folder_name} <strong>{g.qty}</strong></span>
                ))}
                {r.reserved > 0 && <span data-testid={`alloc-reserved-${r.variety_key}`}>Reserved <strong>{r.reserved}</strong></span>}
                <span className="text-[#1F5B3F]" data-testid={`alloc-available-${r.variety_key}`}>Available to sell <strong>{r.available}</strong></span>
                {r.over_allocated > 0 && (
                  <span className="text-[#A33030] font-semibold" data-testid={`alloc-over-${r.variety_key}`}>Over-allocated by {r.over_allocated}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {allocating && (
        <AllocateModal row={allocating} folders={board.folders} onClose={() => setAllocating(null)}
          onDone={async () => { setAllocating(null); await reload(); }} />
      )}
      {history && <HistoryModal history={history} onClose={() => setHistory(null)} />}
    </section>
  );
};

export const AllocateModal = ({ row, folders, onClose, onDone }) => {
  const [dest, setDest] = useState(folders[0]?.id || "new");
  const [newName, setNewName] = useState("");
  const [qty, setQty] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const n = parseInt(qty, 10);
    if (!Number.isFinite(n) || n <= 0) { toast.error("How many tubers?"); return; }
    setBusy(true);
    try {
      await api.post("/admin/allocation/allocate", {
        name: row.name,
        qty: n,
        folder_id: dest === "new" ? null : dest,
        new_folder_name: dest === "new" ? newName.trim() : null,
      });
      toast.success(`${n} × ${row.name} allocated.`);
      await onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't allocate that."); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/40 grid place-items-center p-4" onClick={onClose} data-testid="allocate-modal">
      <div className="bg-[#F8F4EA] border-2 w-full max-w-sm p-5 space-y-3" style={{ borderColor: AUB }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase font-semibold text-[#6E1E3A]">Allocate</div>
            <h3 className="font-serif text-2xl text-[#3C2A1E]">{row.name}</h3>
            <div className="text-xs text-[#7A6A55]">{row.available} available of {row.final} audited</div>
          </div>
          <button type="button" onClick={onClose} data-testid="allocate-close"><X className="w-5 h-5" /></button>
        </div>
        <label className="block">
          <span className="text-[10px] tracking-[0.25em] uppercase text-[#9C8B73]">Destination</span>
          <select value={dest} onChange={(e) => setDest(e.target.value)} data-testid="allocate-destination"
            className="mt-1 w-full h-11 px-2 border rounded bg-white text-sm" style={{ borderColor: EDGE }}>
            {folders.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
            <option value="new">+ Create a new folder…</option>
          </select>
        </label>
        {dest === "new" && (
          <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Heritage Growing 2026/27"
            data-testid="allocate-new-folder" className="w-full h-11 px-3 border rounded text-sm" style={{ borderColor: EDGE }} />
        )}
        <label className="block">
          <span className="text-[10px] tracking-[0.25em] uppercase text-[#9C8B73]">Quantity</span>
          <input type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="allocate-qty"
            className="mt-1 w-full h-11 px-3 border rounded text-sm" style={{ borderColor: EDGE }} />
        </label>
        <p className="text-[11px] italic text-[#7A6A55]">
          Available to Sell is always what's left over , it updates the moment you save.
        </p>
        <button type="button" onClick={submit} disabled={busy} data-testid="allocate-save"
          className="w-full h-12 text-white text-[11px] font-bold uppercase tracking-[0.14em] disabled:opacity-40"
          style={{ backgroundColor: "#6E1E3A" }}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "Allocate tubers"}
        </button>
      </div>
    </div>
  );
};

const HistoryModal = ({ history, onClose }) => (
  <div className="fixed inset-0 z-[70] bg-black/40 grid place-items-center p-4" onClick={onClose} data-testid="allocation-history-modal">
    <div className="bg-[#F8F4EA] border-2 w-full max-w-lg max-h-[80vh] overflow-y-auto p-5" style={{ borderColor: AUB }} onClick={(e) => e.stopPropagation()}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase font-semibold text-[#6E1E3A]">Allocation history</div>
          <h3 className="font-serif text-2xl text-[#3C2A1E]">{history.name}</h3>
        </div>
        <button type="button" onClick={onClose} data-testid="allocation-history-close"><X className="w-5 h-5" /></button>
      </div>
      {history.movements === null ? (
        <div className="mt-4 text-sm text-[#7A6A55] inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : history.movements.length === 0 ? (
        <p className="mt-4 text-sm text-[#7A6A55]">No movements recorded yet.</p>
      ) : (
        <ul className="mt-3 space-y-1.5 text-sm">
          {history.movements.map((m) => (
            <li key={m.id} className="border-b pb-1.5" style={{ borderColor: EDGE }}>
              <strong>{m.qty}</strong> · {m.from_label} → <strong>{m.to_label}</strong>
              <div className="text-[11px] text-[#9C8B73]">{new Date(m.created_at).toLocaleString("en-NZ")} · {m.who}{m.note ? ` · ${m.note}` : ""}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  </div>
);

export const GrowingFolders = ({ board, reload, onPrint }) => {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [renaming, setRenaming] = useState(null);
  const [moving, setMoving] = useState(null);

  const create = async () => {
    if (!name.trim()) { toast.error("Name the folder first."); return; }
    setBusy(true);
    try {
      await api.post("/admin/allocation/folders", { name: name.trim() });
      toast.success(`${name.trim()} created.`);
      setName("");
      await reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't create that folder."); }
    finally { setBusy(false); }
  };

  const rename = async (folder, newName) => {
    try {
      await api.patch(`/admin/allocation/folders/${folder.id}`, { name: newName });
      toast.success("Folder renamed.");
      setRenaming(null);
      await reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't rename that."); }
  };

  const remove = async (folder) => {
    if (!window.confirm(`Delete ${folder.name}? Its ${folder.total} tuber${folder.total === 1 ? "" : "s"} go straight back to Available to Sell.`)) return;
    try {
      const r = await api.delete(`/admin/allocation/folders/${folder.id}`);
      toast.success(`${folder.name} deleted , ${r.data.released_tubers} tubers released.`);
      await reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't delete that."); }
  };

  return (
    <section className="space-y-3" data-testid="growing-folders">
      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase font-semibold text-[#6E1E3A]">Growing / allocation folders</div>
        <p className="text-xs italic text-[#7A6A55]">Heritage growing, mum's garden, breeding patch , anything you're holding back. Allocated tubers leave the sell list automatically.</p>
      </div>
      <div className="flex flex-wrap gap-2">
        <input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && create()}
          placeholder="New folder name…" data-testid="folder-name-input"
          className="flex-1 min-w-[200px] h-11 px-3 border rounded text-sm bg-white" style={{ borderColor: EDGE }} />
        <button type="button" onClick={create} disabled={busy} data-testid="folder-create"
          className="h-11 px-4 text-white text-[11px] font-bold uppercase tracking-[0.14em] inline-flex items-center gap-1.5 disabled:opacity-40"
          style={{ backgroundColor: "#2F6B4F" }}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FolderPlus className="w-4 h-4" />} Create folder
        </button>
      </div>
      {(board?.folders || []).length === 0 ? (
        <div className="bg-white border border-dashed rounded p-6 text-center text-sm text-[#7A6A55]" style={{ borderColor: EDGE }} data-testid="folders-empty">
          No folders yet , create one and start allocating tubers into it.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {board.folders.map((f) => (
            <article key={f.id} className="bg-white border rounded p-4" style={{ borderColor: EDGE }} data-testid={`folder-${f.id}`}>
              <div className="flex items-start gap-2">
                {renaming === f.id ? (
                  <RenameField initial={f.name} onCancel={() => setRenaming(null)} onSave={(v) => rename(f, v)} />
                ) : (
                  <h3 className="font-serif text-xl text-[#3C2A1E] flex-1">{f.name}</h3>
                )}
                <span className="text-[10px] uppercase tracking-[0.12em] px-2 py-0.5 rounded bg-[#EAF1EA] text-[#2F6B4F] font-semibold" data-testid={`folder-total-${f.id}`}>
                  {f.total} tubers
                </span>
              </div>
              <div className="mt-2 space-y-1 text-sm">
                {f.lines.length === 0 ? (
                  <div className="text-[#9C8B73] italic text-xs">Nothing allocated here yet.</div>
                ) : f.lines.map((l) => (
                  <div key={l.id} className="flex items-center gap-2" data-testid={`folder-line-${l.id}`}>
                    <span className="flex-1 text-[#3C2A1E]">{l.name}</span>
                    <strong>{l.qty}</strong>
                    <button type="button" onClick={() => setMoving({ folder: f, line: l })} data-testid={`folder-move-${l.id}`}
                      className="text-[#3E1F46] hover:underline text-[10px] uppercase tracking-[0.12em] font-semibold inline-flex items-center gap-1">
                      <ArrowRightLeft className="w-3 h-3" /> Move
                    </button>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex gap-2 flex-wrap">
                {onPrint && (
                  <button type="button" onClick={() => onPrint(f)} data-testid={`folder-print-${f.id}`}
                    className={btn + " text-white"} style={{ backgroundColor: "#6E1E3A" }}><Printer className="w-3.5 h-3.5" /> Print folder</button>
                )}
                <button type="button" onClick={() => setRenaming(f.id)} data-testid={`folder-rename-${f.id}`}
                  className={btn + " border text-[#3C2A1E]"} style={{ borderColor: EDGE }}><Pencil className="w-3.5 h-3.5" /> Rename</button>
                <button type="button" onClick={() => remove(f)} data-testid={`folder-delete-${f.id}`}
                  className={btn + " border border-[#A33030] text-[#A33030]"}><Trash2 className="w-3.5 h-3.5" /> Delete</button>
              </div>
            </article>
          ))}
        </div>
      )}
      {moving && (
        <MoveModal moving={moving} folders={board.folders} onClose={() => setMoving(null)}
          onDone={async () => { setMoving(null); await reload(); }} />
      )}
    </section>
  );
};

const RenameField = ({ initial, onSave, onCancel }) => {
  const [v, setV] = useState(initial);
  return (
    <div className="flex-1 flex gap-1">
      <input value={v} onChange={(e) => setV(e.target.value)} data-testid="folder-rename-input"
        className="flex-1 h-9 px-2 border rounded text-sm" style={{ borderColor: EDGE }} />
      <button type="button" onClick={() => onSave(v.trim())} data-testid="folder-rename-save" className="w-9 h-9 grid place-items-center text-[#2F6B4F]"><Check className="w-4 h-4" /></button>
      <button type="button" onClick={onCancel} className="w-9 h-9 grid place-items-center text-[#9C8B73]"><X className="w-4 h-4" /></button>
    </div>
  );
};

const MoveModal = ({ moving, folders, onClose, onDone }) => {
  const { folder, line } = moving;
  const [to, setTo] = useState("available");
  const [qty, setQty] = useState(String(line.qty));
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const n = parseInt(qty, 10);
    if (!Number.isFinite(n) || n <= 0) { toast.error("How many are you moving?"); return; }
    setBusy(true);
    try {
      await api.post("/admin/allocation/move", { name: line.name, qty: n, from_folder: folder.id, to_folder: to });
      toast.success(`${n} × ${line.name} moved.`);
      await onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't move that."); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/40 grid place-items-center p-4" onClick={onClose} data-testid="move-modal">
      <div className="bg-[#F8F4EA] border-2 w-full max-w-sm p-5 space-y-3" style={{ borderColor: AUB }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase font-semibold text-[#6E1E3A]">Move stock</div>
            <h3 className="font-serif text-2xl text-[#3C2A1E]">{line.name}</h3>
            <div className="text-xs text-[#7A6A55]">From {folder.name} · {line.qty} there now</div>
          </div>
          <button type="button" onClick={onClose} data-testid="move-close"><X className="w-5 h-5" /></button>
        </div>
        <label className="block">
          <span className="text-[10px] tracking-[0.25em] uppercase text-[#9C8B73]">Move to</span>
          <select value={to} onChange={(e) => setTo(e.target.value)} data-testid="move-destination"
            className="mt-1 w-full h-11 px-2 border rounded bg-white text-sm" style={{ borderColor: EDGE }}>
            <option value="available">Available to sell</option>
            {folders.filter((f) => f.id !== folder.id).map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-[10px] tracking-[0.25em] uppercase text-[#9C8B73]">Quantity</span>
          <input type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="move-qty"
            className="mt-1 w-full h-11 px-3 border rounded text-sm" style={{ borderColor: EDGE }} />
        </label>
        <button type="button" onClick={submit} disabled={busy} data-testid="move-save"
          className="w-full h-12 text-white text-[11px] font-bold uppercase tracking-[0.14em] disabled:opacity-40"
          style={{ backgroundColor: AUB }}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "Move tubers"}
        </button>
      </div>
    </div>
  );
};
